'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { MY_PM_ONBOARDING_SESSION, PM_ONBOARDING_COMPLETE_STEP, PM_ONBOARDING_NAVIGATE_TO_STEP } from '@gql/pm-onboarding';
import type { MyPmOnboardingSessionQuery, PmOnboardingCompleteStepMutation, PmOnboardingNavigateToStepMutation, PmOnboardingReviewReason, PmOnboardingSessionFieldsFragment, PmOnboardingSessionStatus } from '@gql';
import { PmOnboardingStepKey } from '@gql';
export type { PmOnboardingReviewReason, PmOnboardingSessionStatus };
export { PmOnboardingStepKey };
export interface PmOnboardingFlowStep {
  stepKey: string;
  position: number;
  label: string;
  required: boolean;
}
export interface PmOnboardingFlow {
  id: string;
  version: string;
  name: string;
  description?: string;
  isActive: boolean;
  totalSteps: number;
  steps: PmOnboardingFlowStep[];
}
export interface PmOnboardingStepCompletion {
  stepKey: string;
  completedAt: string;
}
export interface PmOnboardingSessionAccount {
  id: string;
  isUsBased?: boolean | null;
  countryOrRegionLongName?: string | null;
}
export interface PmOnboardingSession {
  id: string;
  accountId?: string;
  userId?: string;
  pmsType?: string;
  currentStepKey: string;
  status: PmOnboardingSessionStatus;
  reviewReason?: PmOnboardingReviewReason | null;
  publishedAt?: string;
  startedAt: string;
  completedAt?: string;
  lastActivityAt: string;
  currentStepIndex: number;
  totalSteps: number;
  completedStepKeys: string[];
  canAdvance: boolean;
  flow: PmOnboardingFlow;
  stepCompletions: PmOnboardingStepCompletion[];
  account?: PmOnboardingSessionAccount | null;
}
export interface CompleteStepInput {
  sessionId: string;
  stepKey: PmOnboardingStepKey;
}

/**
 * Type guard to check if a query/mutation result is a PmOnboardingSession.
 * Uses __typename to discriminate between union members at runtime.
 */
function isPmOnboardingSession(result: unknown): result is PmOnboardingSessionFieldsFragment & {
  __typename: 'PmOnboardingSession';
} {
  return typeof result === 'object' && result !== null && '__typename' in result && (result as {
    __typename: string;
  }).__typename === 'PmOnboardingSession';
}

/**
 * Type guard to check if a result is a NotFoundError.
 */
function isNotFoundError(result: unknown): result is {
  __typename: 'NotFoundError';
  message: string;
} {
  return typeof result === 'object' && result !== null && '__typename' in result && (result as {
    __typename: string;
  }).__typename === 'NotFoundError';
}

/**
 * Type guard to check if a result is a ValidationErrorCollection.
 */
function isValidationErrorCollection(result: unknown): result is {
  __typename: 'ValidationErrorCollection';
  errors: Array<{
    message?: string;
  }>;
} {
  return typeof result === 'object' && result !== null && '__typename' in result && (result as {
    __typename: string;
  }).__typename === 'ValidationErrorCollection';
}
interface UsePmOnboardingSessionOptions {
  /**
   * Skip the query entirely. Useful when you want to control when to fetch.
   */
  skip?: boolean;
}
interface UsePmOnboardingSessionResult {
  session: PmOnboardingSession | null;
  loading: boolean;
  error: Error | null;
  notFound: boolean;
  completeStep: (stepKey: PmOnboardingStepKey, sessionIdOverride?: string) => Promise<PmOnboardingSession | null>;
  navigateToStep: (stepKey: PmOnboardingStepKey, sessionIdOverride?: string) => Promise<PmOnboardingSession | null>;
  refetch: () => Promise<unknown>;
}
export function usePmOnboardingSession(t0) {
  const $ = _c(24);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    skip: t2
  } = options;
  const skip = t2 === undefined ? false : t2;
  let t3;
  if ($[2] !== skip) {
    t3 = {
      skip,
      fetchPolicy: "cache-and-network"
    };
    $[2] = skip;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: sessionData,
    loading,
    error,
    refetch: sessionRefetch
  } = useQuery(MY_PM_ONBOARDING_SESSION, t3);
  const [completeStepMutation] = useMutation(PM_ONBOARDING_COMPLETE_STEP);
  const [navigateToStepMutation] = useMutation(PM_ONBOARDING_NAVIGATE_TO_STEP);
  const queryResult = (sessionData as MyPmOnboardingSessionQuery | undefined)?.myPmOnboardingSession;
  let t4;
  bb0: {
    if (!queryResult) {
      t4 = null;
      break bb0;
    }
    if (isPmOnboardingSession(queryResult)) {
      t4 = queryResult;
      break bb0;
    }
    t4 = null;
  }
  const session = t4;
  let t5;
  if ($[4] !== queryResult) {
    t5 = isNotFoundError(queryResult);
    $[4] = queryResult;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const notFound = t5;
  let t6;
  if ($[6] !== sessionRefetch) {
    t6 = () => sessionRefetch();
    $[6] = sessionRefetch;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const refetch = t6;
  let t7;
  if ($[8] !== completeStepMutation || $[9] !== refetch || $[10] !== session?.id) {
    t7 = async (stepKey, sessionIdOverride) => {
      const sessionId = sessionIdOverride || session?.id;
      if (!sessionId) {
        throw new Error("No active session");
      }
      const result = await completeStepMutation({
        variables: {
          input: {
            sessionId,
            stepKey
          }
        }
      });
      const data = (result.data as PmOnboardingCompleteStepMutation | undefined)?.pmOnboardingCompleteStep;
      if (isPmOnboardingSession(data)) {
        await refetch();
        return data;
      }
      if (isValidationErrorCollection(data)) {
        throw new Error(data.errors[0]?.message || "Failed to complete step");
      }
      if (isNotFoundError(data)) {
        throw new Error("Session not found");
      }
      return null;
    };
    $[8] = completeStepMutation;
    $[9] = refetch;
    $[10] = session?.id;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const completeStep = t7;
  let t8;
  if ($[12] !== navigateToStepMutation || $[13] !== refetch || $[14] !== session?.id) {
    t8 = async (stepKey_0, sessionIdOverride_0) => {
      const sessionId_0 = sessionIdOverride_0 || session?.id;
      if (!sessionId_0) {
        throw new Error("No active session");
      }
      const result_0 = await navigateToStepMutation({
        variables: {
          sessionId: sessionId_0,
          stepKey: stepKey_0
        }
      });
      const data_0 = (result_0.data as PmOnboardingNavigateToStepMutation | undefined)?.pmOnboardingNavigateToStep;
      if (isPmOnboardingSession(data_0)) {
        await refetch();
        return data_0;
      }
      if (isValidationErrorCollection(data_0)) {
        throw new Error(data_0.errors[0]?.message || "Failed to navigate");
      }
      return null;
    };
    $[12] = navigateToStepMutation;
    $[13] = refetch;
    $[14] = session?.id;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const navigateToStep = t8;
  const t9 = error || null;
  let t10;
  if ($[16] !== completeStep || $[17] !== loading || $[18] !== navigateToStep || $[19] !== notFound || $[20] !== refetch || $[21] !== session || $[22] !== t9) {
    t10 = {
      session,
      loading,
      error: t9,
      notFound,
      completeStep,
      navigateToStep,
      refetch
    };
    $[16] = completeStep;
    $[17] = loading;
    $[18] = navigateToStep;
    $[19] = notFound;
    $[20] = refetch;
    $[21] = session;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}